import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Dialog, DialogContent } from '../ui/dialog';
import { Search, User, Calendar, Loader2, X } from 'lucide-react';

const useSearch = (term) => {
  const { API, getAuthHeaders } = useAuth();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (term.trim().length < 2) { setResults(null); return undefined; }
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/search`, { params: { q: term }, ...getAuthHeaders() })
        .then((res) => setResults(res.data))
        .catch(() => setResults(null))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  return { results, loading };
};

const ResultList = ({ results, loading, term, onPick }) => {
  const navigate = useNavigate();
  const go = (path) => { onPick(); navigate(path); };

  if (term.trim().length < 2) {
    return <p className="p-4 text-sm text-muted-foreground">Scrivi almeno due lettere: nome, telefono o email.</p>;
  }
  if (loading && !results) {
    return <div className="p-4 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  }
  const customers = results?.customers || [];
  const appointments = results?.appointments || [];
  if (!customers.length && !appointments.length) {
    return <p className="p-4 text-sm text-muted-foreground" data-testid="search-empty">Nessun risultato per "{term}".</p>;
  }

  return (
    <div className="max-h-[60vh] overflow-y-auto py-1">
      {customers.length > 0 && (
        <>
          <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Clienti</p>
          {customers.map((c) => (
            <button key={c.id} onClick={() => go(`/customers?q=${encodeURIComponent(c.phone || c.name)}`)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted text-left transition-colors"
              data-testid={`search-customer-${c.id}`}>
              <User className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{c.name}</span>
                <span className="block text-xs text-muted-foreground truncate">{c.phone || c.email}</span>
              </span>
            </button>
          ))}
        </>
      )}
      {appointments.length > 0 && (
        <>
          <p className="px-3 pt-2 pb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Appuntamenti</p>
          {appointments.map((a) => (
            <button key={a.id} onClick={() => go(`/calendar?date=${(a.start_time || '').slice(0, 10)}`)}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted text-left transition-colors"
              data-testid={`search-appointment-${a.id}`}>
              <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">
                  {a.title}{a.customer_name ? ` · ${a.customer_name}` : ''}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {a.start_time ? format(parseISO(a.start_time), "EEEE d MMMM 'alle' HH:mm", { locale: itLocale }) : ''}
                </span>
              </span>
            </button>
          ))}
        </>
      )}
    </div>
  );
};

/** Ricerca rapida: campo nella barra su desktop, icona + finestra su telefono. */
export const GlobalSearch = ({ variant = 'bar' }) => {
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const { results, loading } = useSearch(term);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const reset = () => { setTerm(''); setOpen(false); };

  if (variant === 'icon') {
    return (
      <>
        <Button variant="ghost" size="icon" onClick={() => setOpen(true)} data-testid="global-search-open">
          <Search className="w-5 h-5" />
        </Button>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTerm(''); }}>
          <DialogContent className="p-0 gap-0 top-[12%] translate-y-0 sm:max-w-md" data-testid="global-search-dialog">
            <div className="flex items-center gap-2 border-b px-3">
              <Search className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                autoFocus value={term} onChange={(e) => setTerm(e.target.value)}
                placeholder="Cerca un cliente o un appuntamento"
                className="flex-1 h-12 bg-transparent text-sm outline-none"
                data-testid="global-search-input-mobile"
              />
              {term && (
                <button onClick={() => setTerm('')} className="p-1 text-muted-foreground" data-testid="search-clear">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <ResultList results={results} loading={loading} term={term} onPick={reset} />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  return (
    <div className="relative w-64 xl:w-80" ref={boxRef}>
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <Input
        value={term}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        placeholder="Cerca cliente o appuntamento"
        className="pl-9 h-10 rounded-full bg-muted/60 border-transparent focus-visible:bg-background"
        data-testid="global-search-input"
      />
      {open && term.trim().length > 0 && (
        <div className="absolute top-12 left-0 w-[22rem] rounded-xl border border-border bg-popover shadow-lg z-50 overflow-hidden"
          data-testid="global-search-results">
          <ResultList results={results} loading={loading} term={term} onPick={reset} />
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
